import React, { useRef, useState, useCallback } from 'react';
import { Upload, FileText, X } from 'lucide-react';

/**
 * Zona de drag & drop para arquivos. Converte o arquivo escolhido em base64
 * (puro, sem o prefixo data:...;base64,) e entrega via onFile.
 *
 * Props:
 * - onFile(file: { name, mime_type, base64, size }) — callback com o arquivo
 * - accept: string de tipos aceitos (default: '.pdf,.jpg,.jpeg,.png,.tiff')
 * - maxSizeMB: limite em MB (default: 20)
 * - label: texto principal do estado vazio
 * - disabled: boolean
 */
export default function FileDropZone({
  onFile,
  accept = '.pdf,.jpg,.jpeg,.png,.tiff',
  maxSizeMB = 20,
  label = 'Anexar documento',
  disabled = false,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const processFile = useCallback(
    (file) => {
      setError(null);
      if (!file) return;
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`Arquivo muito grande (max ${maxSizeMB}MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 = typeof result === 'string' ? result.split(',')[1] || '' : '';
        setSelectedFile({ name: file.name, size: file.size, type: file.type });
        onFile({
          name: file.name,
          mime_type: file.type || 'application/pdf',
          base64,
          size: file.size,
        });
      };
      reader.onerror = () => setError('Erro ao ler arquivo');
      reader.readAsDataURL(file);
    },
    [onFile, maxSizeMB]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const clear = () => {
    setSelectedFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
    onFile(null);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
        dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-slate-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
      />

      {selectedFile ? (
        <div className="flex items-center justify-center gap-2 text-xs text-slate-700">
          <FileText size={16} className="text-blue-600 flex-shrink-0" />
          <span className="font-medium truncate max-w-[240px]" title={selectedFile.name}>{selectedFile.name}</span>
          <span className="text-slate-400 flex-shrink-0">({(selectedFile.size / 1024).toFixed(0)} KB)</span>
          <button
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="p-0.5 hover:bg-slate-200 rounded flex-shrink-0"
            title="Remover arquivo"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div className="text-xs text-slate-500">
          <Upload size={20} className="mx-auto mb-1 text-slate-400" />
          <div className="font-medium text-slate-700">{label}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            Arraste PDF/imagem ou clique para selecionar (max {maxSizeMB}MB)
          </div>
        </div>
      )}

      {error && (
        <div className="text-[11px] text-red-600 mt-1">{error}</div>
      )}
    </div>
  );
}
